import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNotification } from '@/hooks';
import { NotificationSeverity } from '@/components/notifications/Notification';
import {
  useGetBranchByIdQuery,
  useUpdateBranchAddressMutation,
} from '@/pages/Business/pages/PickupCycles/services';
import {
  BranchAddressFormValues,
  branchAddressSchema,
  defaultBranchAddressValues,
  UpdateBranchAddressPayload,
} from '@/pages/Business/pages/PickupCycles/models';
import { getApiErrorMessage } from '@/pages/Business/pages/PickupCycles/utils';

interface UseBranchAddressFormProps {
  branchId: string | null;
  open: boolean;
  onSaved?: () => void;
}

export const useBranchAddressForm = ({
  branchId,
  open,
  onSaved,
}: UseBranchAddressFormProps) => {
  const { showNotification } = useNotification();

  const {
    data: branch,
    isFetching: isLoadingBranch,
  } = useGetBranchByIdQuery(
    { id: branchId ?? '' },
    { skip: !branchId || !open },
  );

  const [updateBranchAddress, { isLoading: isSaving }] =
    useUpdateBranchAddressMutation();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<BranchAddressFormValues>({
    resolver: zodResolver(branchAddressSchema),
    defaultValues: defaultBranchAddressValues,
  });

  useEffect(() => {
    if (!open) return;

    if (branch?.address) {
      reset({
        streetName: branch.address.streetName ?? '',
        doorNumber: branch.address.doorNumber ?? '',
        apartmentNumber: branch.address.apartmentNumber ?? '',
        corner: branch.address.corner ?? '',
        zipCode: branch.address.zipCode ?? '',
        neighborhoodId: branch.address.neighborhoodId ?? '',
      });
      return;
    }

    reset(defaultBranchAddressValues);
  }, [branch, open, reset]);

  const onFormSubmit = handleSubmit(async (data) => {
    if (!branch) {
      showNotification(
        'No se pudo cargar la sucursal',
        NotificationSeverity.error,
      );
      return;
    }

    const body: UpdateBranchAddressPayload = {
      name: branch.name,
      businessName: branch.businessName ?? '',
      commerceId: branch.commerceId,
      streetName: data.streetName.trim(),
      doorNumber: data.doorNumber.trim(),
      ...(data.apartmentNumber?.trim() && {
        apartmentNumber: data.apartmentNumber.trim(),
      }),
      ...(data.corner?.trim() && { corner: data.corner.trim() }),
      ...(data.zipCode?.trim() && { zipCode: data.zipCode.trim() }),
      ...(data.neighborhoodId?.trim() && {
        neighborhoodId: data.neighborhoodId.trim(),
      }),
    };

    try {
      await updateBranchAddress({ id: branch.id, body }).unwrap();
      showNotification('Dirección guardada', NotificationSeverity.success);
      onSaved?.();
    } catch (error) {
      showNotification(
        getApiErrorMessage(error, 'No se pudo guardar la dirección'),
        NotificationSeverity.error,
      );
    }
  });

  return {
    control,
    onFormSubmit,
    setNeighborhoodId: (value: string) => setValue('neighborhoodId', value),
    neighborhoodId: watch('neighborhoodId'),
    branchName: branch?.name ?? '',
    isLoadingBranch,
    isSaving,
  };
};
